import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Users, Stethoscope, Pill, Activity, Receipt, Sparkles, History, LogOut, UserCog, FileText, Database, Menu, X, Search } from 'lucide-react';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasRole } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const allNavItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'staff', 'doctor'] },
    { path: '/patients', label: 'Patients', icon: Users, roles: ['admin', 'staff', 'doctor'] },
    { path: '/patient-lookup', label: 'Patient Lookup', icon: Search, roles: ['admin', 'staff', 'doctor'] },
    { path: '/doctors', label: 'Doctors', icon: Stethoscope, roles: ['admin'] },
    { path: '/medicines', label: 'Medicines', icon: Pill, roles: ['admin', 'staff'] },
    { path: '/procedures', label: 'Procedures', icon: Activity, roles: ['admin', 'doctor'] },
    { path: '/consultations', label: 'Consultations', icon: Stethoscope, roles: ['admin', 'staff', 'doctor'] },
    { path: '/billing', label: 'Billing', icon: Receipt, roles: ['admin', 'staff'] },
    { path: '/ai-features', label: 'AI Features', icon: Sparkles, roles: ['admin', 'staff', 'doctor'] },
    { path: '/purchase-history', label: 'Purchase History', icon: History, roles: ['admin', 'staff'] },
    { path: '/users', label: 'Users', icon: UserCog, roles: ['admin'] },
    { path: '/audit-logs', label: 'Audit Trail', icon: FileText, roles: ['admin'] },
    { path: '/cleanup', label: 'Data Cleanup', icon: Database, roles: ['admin'] },
  ];

  const navItems = allNavItems.filter((item) => hasRole(item.roles));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  const roleColors = {
    admin: { bg: '#FEE2E2', color: '#991B1B' },
    staff: { bg: '#DBEAFE', color: '#1E40AF' },
    doctor: { bg: '#D1FAE5', color: '#065F46' },
  };

  const roleBadge = roleColors[user?.role] || roleColors.staff;

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#F9F6F0' }}>
      {/* Mobile top bar */}
      <header className="mobile-topbar" data-testid="mobile-topbar">
        <button
          onClick={() => setMobileNavOpen(true)}
          className="p-2 rounded-lg"
          style={{ color: '#990099' }}
          aria-label="Open menu"
          data-testid="mobile-menu-btn"
        >
          <Menu size={24} />
        </button>
        <div className="flex items-center gap-2">
          <img
            src="https://customer-assets.emergentagent.com/job_jantikar-clinic/artifacts/b0xcrudr_logo.jpeg"
            alt="Jantikar Ayurveda"
            className="w-8 h-8 object-contain"
          />
          <span className="font-bold" style={{ color: '#990099', fontFamily: 'Outfit, sans-serif' }}>
            Jantikar Ayurveda
          </span>
        </div>
        <div style={{ width: 40 }} />
      </header>

      {/* Sidebar overlay (mobile) */}
      {mobileNavOpen && (
        <div
          className="sidebar-overlay"
          onClick={closeMobileNav}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        className={`app-sidebar ${mobileNavOpen ? 'open' : ''}`}
        style={{ backgroundColor: 'white', borderColor: '#E5E0D8' }}
        data-testid="app-sidebar"
      >
        <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: '#E5E0D8' }}>
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_jantikar-clinic/artifacts/b0xcrudr_logo.jpeg"
              alt="Jantikar Ayurveda"
              className="w-12 h-12 object-contain"
              data-testid="sidebar-logo"
            />
            <div>
              <h1 className="text-xl font-bold leading-tight" style={{ color: '#990099', fontFamily: 'Outfit, sans-serif' }}>
                Jantikar Ayurveda
              </h1>
              <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Clinic Management</p>
            </div>
          </div>
          <button
            onClick={closeMobileNav}
            className="sidebar-close-btn p-1 rounded"
            aria-label="Close menu"
            data-testid="sidebar-close-btn"
          >
            <X size={22} style={{ color: '#6B7280' }} />
          </button>
        </div>

        <nav className="p-4 flex-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMobileNav}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors"
                style={{
                  backgroundColor: isActive ? '#F5E6F5' : 'transparent',
                  color: isActive ? '#990099' : '#2D3A2A',
                  fontWeight: isActive ? '500' : '400',
                }}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t" style={{ borderColor: '#E5E0D8' }} data-testid="user-panel">
          <div className="px-2 py-3 rounded-lg mb-2" style={{ backgroundColor: '#F9F6F0' }}>
            <p className="text-sm font-medium truncate" style={{ color: '#2D3A2A' }} data-testid="user-name">
              {user?.name}
            </p>
            <p className="text-xs truncate mt-0.5" style={{ color: '#6B7280' }}>{user?.email}</p>
            <span
              className="inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium uppercase tracking-wide"
              style={{ backgroundColor: roleBadge.bg, color: roleBadge.color }}
              data-testid="user-role-badge"
            >
              {user?.role}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2 rounded-lg transition-colors hover:bg-red-50"
            style={{ color: '#EF4444' }}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      <main className="app-main flex-1 overflow-auto">
        <div className="app-main-inner">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
